import {Alert, Badge, Group, Loader, Stack, Table, Text, TextInput} from '@mantine/core';
import {AiOutlineWarning} from 'react-icons/ai';
import type {NameAvailability, NameAvailabilityResult} from '../../devops/name-availability';

type DerivedNamesPanelProps = {
    result?: NameAvailabilityResult,
    isFetching: boolean,
    // Per-name overrides, keyed by the derived name's `kind`. A name the
    // operator has edited is theirs, not the convention's.
    overrides: Record<string, string>,
    onOverride: (kind: string, value: string) => void,
};

// FNP-1500: EVERY DERIVED NAME, VISIBLE AND EDITABLE, BEFORE ANYTHING IS BUILT.
//
// ═══ THE FAILURE THIS PREVENTS ═══
//
// Provisioning derives two repository names, two pipeline names, a stack name
// per stage and a deployment bucket from ONE base name, and checked none of
// them. A collision first surfaced at CreateInfraRepo — step 7 of 11 — after a
// repository had already been created, leaving a FAILED record and a PENDING
// inventory row that nothing cleans up.
//
// The owner hit exactly this on 2026-09-13. The refusal for `flexi-testing-app`
// suggested `flexi-testing`, which derives `flexi-testing-app-infra` — a repo
// that already existed. The error message recommended a name that would have
// failed differently, later, and messier.
//
// ⚠️ UNKNOWN IS NOT FREE. A listing that fails permissions returns zero items
// and is indistinguishable from an empty account, so a lookup that could not
// complete is shown as unknown and blocks Provision exactly as a collision
// does. Rendering it as free would be the same silent-success class the
// teardown's `verify-infrastructure-absent` was written to avoid.
const statusBadge = (status: NameAvailability['status']) => {
    if (status === 'FREE') {
        return <Badge color={'green'} variant={'light'}>Free</Badge>;
    }
    if (status === 'TAKEN') {
        return <Badge color={'red'} variant={'light'}>Taken</Badge>;
    }
    return <Badge color={'orange'} variant={'light'}>Could not check</Badge>;
};

export const DerivedNamesPanel = ({
                                      result,
                                      isFetching,
                                      overrides,
                                      onOverride,
                                  }: DerivedNamesPanelProps) => {

    if (!result) {
        return null;
    }

    // A base name the rules already reject gets the reason and nothing else.
    // Showing per-name statuses here would be a fiction — no lookup ran.
    if (result.violations.length > 0) {
        return (
            <Alert variant={'light'} color={'red'} icon={<AiOutlineWarning/>}>
                <Stack gap={'xs'}>
                    {result.violations.map((violation) => (
                        <Text key={violation.rule} size={'sm'}>{violation.message}</Text>
                    ))}
                </Stack>
            </Alert>
        );
    }

    return (
        <Stack gap={'sm'}>
            <Group gap={'xs'}>
                <Text size={'sm'} fw={500}>This will create</Text>
                {isFetching ? <Loader size={'xs'}/> : null}
            </Group>

            <Table withTableBorder verticalSpacing={'xs'}>
                <Table.Tbody>
                    {result.names.map((entry) => (
                        <Table.Tr key={entry.kind}>
                            <Table.Td w={180}>
                                <Text size={'xs'} c={'dimmed'}>
                                    {entry.resourceType}
                                    {entry.stage ? ` (${entry.stage})` : ''}
                                </Text>
                            </Table.Td>
                            <Table.Td>
                                {/*
                                  * ⚠️ EDITABLE, and that is the point. The
                                  * derivation is a SUGGESTION. When one name
                                  * collides the operator overrides that one
                                  * name rather than being forced to rename
                                  * everything — which is what would have let
                                  * the testing application reuse its existing
                                  * infra repo instead of picking a new base.
                                  */}
                                <TextInput
                                    size={'xs'}
                                    value={overrides[entry.kind] ?? entry.name}
                                    onChange={(e) => onOverride(entry.kind, e.currentTarget.value)}
                                    error={entry.status === 'TAKEN'}
                                    data-testid={`derived-name-${entry.kind}`}
                                />
                            </Table.Td>
                            <Table.Td w={140}>
                                {statusBadge(entry.status)}
                            </Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>

            {result.taken.length > 0 && (
                <Alert variant={'light'} color={'red'} icon={<AiOutlineWarning/>}>
                    <Text size={'sm'}>
                        {result.taken.length === 1 ? 'One name is' : `${result.taken.length} names are`}
                        {' '}already taken. Edit the name above, or choose a
                        different base name.
                    </Text>
                </Alert>
            )}
        </Stack>
    );
};
